/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-03-14 00:00:00
 *
 *******************************************************/
import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FullscreenBar, Icon, TextStyle, Badge, Frame, Spinner, Button } from '@shopify/polaris';
import { ExitMajor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { matchPath } from 'react-router';
import Classnames from 'classnames';

import {
  enterSABFullscreen,
  exitSABFullscreen
} from 'src/integrations/shopify-app-bridge/fullscreen';

import { handleOpenFullscreen } from 'actions/app-integration-actions';

import BoostSnippetManual from './boost-snippet.manual';
import CollectionSearchManual from './collection-search.manual';
import IswManual from './isw.manual';
import RecommendationManual from './recommendation.manual';
import Error404 from 'src/assets/images/shopify-integration/404.svg';

import { TEMPLATE_FEATURE } from 'src/constants/shopify-integration';
import { routes } from 'src/routes';
import history from 'src/routes/history';
import * as themeIntegrationAction from 'actions/shopify-integration/theme-integration-actions';
import * as shopifyIntegrationThemeAction from 'actions/shopify-integration/theme-actions';

type Props = {};
type ComposedProps = Props & WithI18nProps;

type State = {};

class ShopifyIntegrationGuide extends Component<ComposedProps, State> {
  unsubscribeExit = undefined;
  state = {
    themeId: 0,
    templateId: '',
    themeNameDisplay: '',
    isOS20: true,
    error: false
  };

  Components = {
    [routes.shopifyIntegration.guide(':themeId', ':templateId', TEMPLATE_FEATURE.BOOST)]:
      BoostSnippetManual,
    [routes.shopifyIntegration.guide(':themeId', ':templateId', TEMPLATE_FEATURE.COLLECTION)]:
      CollectionSearchManual,
    [routes.shopifyIntegration.guide(':themeId', ':templateId', TEMPLATE_FEATURE.SEARCH)]:
      CollectionSearchManual,
    [routes.shopifyIntegration.guide(':themeId', ':templateId', TEMPLATE_FEATURE.ISW)]: IswManual,
    [routes.shopifyIntegration.guide(':themeId', ':templateId', TEMPLATE_FEATURE.RECOMMENDATION)]:
      RecommendationManual
  };

  async componentDidMount() {
    // Open fullscreen
    this.handleOpenFullscreenPage();

    const { getAllThemes, themes, updateFieldReducer, match } = this.props;

    updateFieldReducer('selectedThemeId', Number(match.params.themeId));
    let themeData = themes;
    if (!themes.length) {
      const result = await getAllThemes();
      themeData = _.get(result, 'payload.data', []);
    }

    const themeDetails = _.find(themeData, { id: Number(match.params.themeId) });
    const themeName = _.get(themeDetails, 'name', '');
    const isOS20 = _.get(themeDetails, 'isOS20', '');
    const OSText = isOS20 ? '(OS 2.0)' : '(OS 1.0)';
    const themeNameDisplay = `${themeName} ${OSText}`;

    if (!themeDetails) {
      this.setState({
        error: true
      });
    } else {
      this.setState({
        themeId: Number(match.params.themeId),
        templateId: match.params.templateId,
        themeNameDisplay: themeNameDisplay,
        isOS20: isOS20
      });
    }

    // Get data from location
    // this.setStateFromLocation();
  }

  componentWillReceiveProps(nextProps) {
    const { themes } = this.props;
    const { themes: nextThemes, match } = nextProps;
    if (themes !== nextThemes) {
      const themeDetails = _.find(nextThemes, { id: Number(match.params.themeId) });
      if (!themeDetails) {
        this.setState({
          themeId: 0,
          templateId: '',
          themeNameDisplay: '',
          error: true
        });
      }
    }
  }

  componentWillUnmount() {
    this.unsubscribeExit();
  }

  renderContent = () => {
    const { location, match } = this.props;
    const { pathname } = location;
    const { themeId, templateId, isOS20 } = this.state;

    const arrKeys = Object.keys(this.Components);
    let Tab = null;
    for (let key of arrKeys) {
      if (
        matchPath(pathname, {
          path: key,
          exact: true,
          strict: false
        })
      ) {
        Tab = this.Components[key];
        this.pageType = pathname.includes(TEMPLATE_FEATURE.COLLECTION) ? 'collection' : 'search';
        break;
      }
    }

    return Tab ? (
      <Tab
        location={location}
        match={match}
        themeId={themeId}
        templateId={templateId}
        isOS20={isOS20}
        pageType={this.pageType}
        backToMainStep={() =>
          history.push(routes.shopifyIntegration.guide(themeId, templateId, TEMPLATE_FEATURE.BOOST))
        }
      />
    ) : null;
  };

  render() {
    const { i18n, getAllThemesLoading } = this.props;
    const { themeNameDisplay, error } = this.state;

    if (getAllThemesLoading) {
      return (
        <div className="BC-ThemeSetting-Editor-Page theme-setting-visual-editor">
          <Frame>
            <Spinner accessibilityLabel="Spinner example" size="large" />
          </Frame>
        </div>
      );
    }

    if (error) {
      return (
        <div className="BC-Page BC-Shopify-Integration-Manual-Install">
          <div className="error-container">
            <img src={Error404} alt="404" />
            <h1 className="title">
              {i18n.translate('AdminHTML.ThemeSetting.Common.themeNoExist')}
            </h1>
            <Button onClick={this.handleCloseGuide}>
              {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Common.backToIntegration')}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="BC-Shopify-Integration-Guide-Page">
        <FullscreenBar>
          <button
            className="BC-BackAction Polaris-FullscreenBar__BackAction"
            onClick={this.handleCloseGuide}>
            <div>
              <Icon source={ExitMajor} color="base" />
            </div>
            <span style={{ paddingLeft: '8px' }}>
              {i18n.translate('AdminHTML.ShopifyIntegration.Actions.exit')}
            </span>
          </button>
          <div className="user-guide-header">
            <div className="title">
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.ShopifyIntegration.Guide.title', {
                  t1: themeNameDisplay
                })}
              </TextStyle>
            </div>
          </div>
        </FullscreenBar>
        <div className="user-guide-body">
          <div className="menu">{this.renderMenu()}</div>
          <div className="content">{this.renderContent()}</div>
        </div>
      </div>
    );
  }

  renderMenu = () => {
    const { i18n, location } = this.props;
    const { themeId, templateId, isOS20 = true } = this.state;

    const menu = [
      {
        id: routes.shopifyIntegration.guide(themeId, templateId, TEMPLATE_FEATURE.BOOST),
        name: isOS20
          ? i18n.translate('AdminHTML.ShopifyIntegration.Guide.menuEnableBoostSnippet')
          : i18n.translate('AdminHTML.ShopifyIntegration.Guide.menuEnableBoostSnippetOS10')
      },
      {
        id: routes.shopifyIntegration.guide(themeId, templateId, TEMPLATE_FEATURE.COLLECTION),
        name: i18n.translate('AdminHTML.ShopifyIntegration.Guide.menuCollectionPage')
      },
      {
        id: routes.shopifyIntegration.guide(themeId, templateId, TEMPLATE_FEATURE.SEARCH),
        name: i18n.translate('AdminHTML.ShopifyIntegration.Guide.menuSearchPage')
      },
      {
        id: routes.shopifyIntegration.guide(themeId, templateId, TEMPLATE_FEATURE.ISW),
        name: i18n.translate('AdminHTML.ShopifyIntegration.Guide.menuISW')
      },
      {
        id: routes.shopifyIntegration.guide(themeId, templateId, TEMPLATE_FEATURE.RECOMMENDATION),
        name: i18n.translate('AdminHTML.ShopifyIntegration.Guide.menuRecommendation')
      }
    ];

    return (
      <ul>
        {_.map(menu, (item, index) => {
          return (
            <li
              key={'menu' + index}
              className={Classnames('menu-item', {
                active: matchPath(location?.pathname, {
                  path: item.id,
                  exact: true,
                  strict: false
                })
              })}
              onClick={() => this.handleSelectMenu(item)}>
              {item.name}
              {index === 0 && (
                <span className="required">
                  <Badge status="attention">
                    {i18n.translate('AdminHTML.ShopifyIntegration.Guide.required')}
                  </Badge>
                </span>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  handleOpenFullscreenPage = () => {
    const { handleOpenFullscreen } = this.props;
    this.unsubscribeExit = enterSABFullscreen(this.handleCloseGuide);
    handleOpenFullscreen(true);
  };

  handleSelectMenu = item => {
    history.push(item.id);
  };

  handleCloseGuide = () => {
    const { themeId } = this.state;

    exitSABFullscreen();
    this.props.handleOpenFullscreen(false);

    // Back to template details
    if (!themeId) {
      history.push({
        pathname: routes.shopifyIntegration.view()
      });
    } else {
      history.push({
        pathname: routes.shopifyIntegration.viewThemeWidget(themeId)
      });
    }
  };
}

const mapStateToProps = (state, props) => {
  const { shopifyIntegrationTheme = {} } = state;
  const themes = _.get(shopifyIntegrationTheme, 'allThemes', []);

  return {
    themes,
    getAllThemesLoading: _.get(shopifyIntegrationTheme, 'getAllThemesLoading', true)
  };
};
const mapDispatchToProps = (dispatch, ownProps) => ({
  handleOpenFullscreen: isOpenFullScreen => dispatch(handleOpenFullscreen(isOpenFullScreen)),
  updateFieldReducer: (fieldName, data) =>
    dispatch(themeIntegrationAction.handleUpdateField(fieldName, data)),
  getAllThemes: () => dispatch(shopifyIntegrationThemeAction.getAllThemes(ownProps.i18n))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(ShopifyIntegrationGuide));
